import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Paper,
  Skeleton,
  useTheme,
  useMediaQuery,
  SwipeableDrawer
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  DateRange as DateIcon,
  LocationOn as LocationIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { GalleryService } from '../../services/galleryService';
import { getPublicGalleryImageUrl } from '../../utils/galleryImageUtils';
import { surface, brand, ink, line } from '../../theme/tokens';

// Scrims over photography: warm near-black so the overlay belongs to the palette.
const scrim = 'rgba(45, 32, 30, 0.55)';
const scrimStrong = 'rgba(45, 32, 30, 0.78)';

// Controls that float over an image: fixed dark chip + on-dark ink, so contrast
// never depends on what the photo happens to look like underneath.
const overlayButtonSx = {
  color: ink.onDark,
  bgcolor: scrim,
  '&:hover': { bgcolor: scrimStrong },
};

const breadcrumbLinkSx = {
  color: ink.secondary,
  fontSize: { xs: '0.875rem', md: '1rem' },
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline', color: ink.primary },
};

const GalleryViewPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { galleryId } = useParams();
  const navigate = useNavigate();
  
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [imageLoading, setImageLoading] = useState({});
  const [loadingOriginal, setLoadingOriginal] = useState(false);

  useEffect(() => {
    loadGallery();
  }, [galleryId]);

  const loadGallery = async () => {
    setLoading(true);
    try {
      const galleryData = await GalleryService.fetchGallery(galleryId);
      setGallery(galleryData);
    } catch (error) {
      console.error('Error loading gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setLoadingOriginal(true);
  };

  const handleCloseViewer = () => {
    setSelectedImageIndex(null);
    setLoadingOriginal(false);
  };

  const handlePrevImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
      setLoadingOriginal(true);
    }
  };

  const handleNextImage = () => {
    if (selectedImageIndex < gallery.images.items.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
      setLoadingOriginal(true);
    }
  };

  const handleKeyDown = (event) => {
    if (selectedImageIndex !== null) {
      switch (event.key) {
        case 'ArrowLeft':
          handlePrevImage();
          break;
        case 'ArrowRight':
          handleNextImage();
          break;
        case 'Escape':
          handleCloseViewer();
          break;
        default:
          break;
      }
    }
  };

  const handleImageLoad = (imageId) => {
    setImageLoading(prev => ({ ...prev, [imageId]: false }));
  };

  const handleImageLoadStart = (imageId) => {
    setImageLoading(prev => ({ ...prev, [imageId]: true }));
  };

  const handleOriginalImageLoad = () => {
    setLoadingOriginal(false);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: gallery.title,
          text: gallery.description,
          url: window.location.href,
        });
      } catch (error) {
        // Fallback to copying URL
        navigator.clipboard.writeText(window.location.href);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const getCategoryLabel = (category) => {
    return GalleryService.getGalleryCategories()
      .find(cat => cat.value === category)?.label || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      FARM_LIFE: 'primary',
      CROPS: 'success',
      HARVEST: 'warning',
      ACTIVITIES: 'info',
      EVENTS: 'secondary',
      NATURE: 'success',
      INFRASTRUCTURE: 'default',
      PEOPLE: 'primary',
      SEASONS: 'info',
      ORGANIC_PROCESS: 'success'
    };
    return colors[category] || 'default';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, gallery]);

  if (loading) {
    return (
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: { xs: 2, md: 4 }, 
          px: { xs: 1, sm: 2, md: 3 } 
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          py: { xs: 3, md: 4 } 
        }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!gallery) {
    return (
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: { xs: 2, md: 4 }, 
          px: { xs: 1, sm: 2, md: 3 } 
        }}
      >
        <Alert 
          severity="error"
          sx={{ 
            fontSize: { xs: '0.875rem', md: '1rem' },
            mx: { xs: 1, md: 0 }
          }}
        >
          Gallery not found
        </Alert>
      </Container>
    );
  }

  const selectedImage = selectedImageIndex !== null ? gallery.images.items[selectedImageIndex] : null;
  const sortedImages = gallery.images.items.sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: { xs: 2, md: 4 }, 
        px: { xs: 1, sm: 2, md: 3 } 
      }}
    >
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ 
        mb: { xs: 2, md: 3 },
        px: { xs: 1, md: 0 }
      }}>
        <Link 
          component={RouterLink} 
          to="/" 
          color="inherit"
          sx={breadcrumbLinkSx}
        >
          Home
        </Link>
        <Link 
          component={RouterLink} 
          to="/gallery" 
          color="inherit"
          sx={breadcrumbLinkSx}
        >
          Gallery
        </Link>
        <Typography 
          color="text.primary"
          sx={{ 
            fontSize: { xs: '0.875rem', md: '1rem' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: { xs: 'nowrap', sm: 'normal' },
            maxWidth: { xs: '150px', sm: 'none' }
          }}
        >
          {gallery.title}
        </Typography>
      </Breadcrumbs>

      {/* Gallery Header */}
      <Paper elevation={0} sx={{ 
        p: { xs: 2.5, md: 4 }, 
        mb: { xs: 3, md: 4 },
        mx: { xs: 1, md: 0 },
        backgroundColor: surface.card,
        border: `1px solid ${line.subtle}`,
        borderTop: `3px solid ${brand.mustard}`,
        borderRadius: 2,
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: { xs: 1.5, md: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom
              sx={{ 
                color: ink.primary,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' },
                mb: { xs: 1.5, md: 2 },
                lineHeight: 1.15
              }}
            >
              {gallery.title}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              mb: { xs: 1.5, md: 2 }, 
              flexWrap: 'wrap' 
            }}>
              <Chip
                label={getCategoryLabel(gallery.category)}
                color={getCategoryColor(gallery.category)}
                size={isMobile ? "small" : "medium"}
                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
              />
              
              {gallery.date && (
                <Chip
                  icon={<DateIcon sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />}
                  label={formatDate(gallery.date)}
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              )}
              
              {gallery.location && (
                <Chip
                  icon={<LocationIcon sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />}
                  label={gallery.location}
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                />
              )}
            </Box>
            
            {gallery.description && (
              <Typography 
                variant="body1" 
                paragraph
                sx={{ 
                  color: ink.secondary,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  lineHeight: 1.7,
                  mb: { xs: 1.5, md: 2 }
                }}
              >
                {gallery.description}
              </Typography>
            )}
            
            <Typography 
              variant="body2" 
              sx={{
                color: ink.muted,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontSize: { xs: '0.75rem', md: '0.8125rem' },
              }}
            >
              {sortedImages.length} {sortedImages.length === 1 ? 'photo' : 'photos'}
            </Typography>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={handleShare}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              ml: { xs: 0, sm: 2 },
              alignSelf: { xs: 'flex-end', sm: 'flex-start' },
              minWidth: { xs: 'auto', sm: 'auto' },
              px: { xs: 2, md: 3 },
              color: ink.primary,
              borderColor: line.strong,
              fontWeight: 600,
              fontSize: { xs: '0.875rem', md: '1rem' },
              '&:hover': { borderColor: brand.brown, backgroundColor: line.subtle }
            }}
          >
            Share
          </Button>
        </Box>
      </Paper>

      {/* Images Grid */}
      {sortedImages.length > 0 ? (
        <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ px: { xs: 1, md: 0 } }}>
          {sortedImages.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '1',
                  borderRadius: { xs: 1, md: 1 },
                  overflow: 'hidden',
                  cursor: 'pointer',
                  bgcolor: surface.sunken,
                  '&:hover .overlay': {
                    opacity: isMobile ? 0 : 1
                  },
                  '&:active .overlay': {
                    opacity: 1
                  }
                }}
                onClick={() => handleImageClick(index)}
              >
                {/* Loading skeleton */}
                {imageLoading[image.id] !== false && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ position: 'absolute', top: 0, left: 0 }}
                  />
                )}
                
                {/* Thumbnail image */}
                <img
                  src={getPublicGalleryImageUrl(image.thumbnailUrl)}
                  alt={image.altText || `Gallery image ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: imageLoading[image.id] === false ? 'block' : 'none'
                  }}
                  onLoad={() => handleImageLoad(image.id)}
                  onLoadStart={() => handleImageLoadStart(image.id)}
                />
                
                {/* Hover overlay */}
                <Box
                  className="overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: scrim,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease-in-out'
                  }}
                >
                  <IconButton 
                    sx={{ 
                      color: ink.onDark,
                      fontSize: { xs: '1.2rem', md: '1.5rem' }
                    }}
                  >
                    <ZoomInIcon fontSize={isMobile ? "medium" : "large"} />
                  </IconButton>
                </Box>
                
                {/* Caption overlay */}
                {image.caption && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: scrimStrong,
                      color: ink.onDark,
                      px: { xs: 1, md: 1.25 },
                      py: { xs: 0.75, md: 1 }
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      noWrap
                      sx={{ 
                        display: 'block',
                        color: ink.onDark,
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        lineHeight: 1.3
                      }}
                    >
                      {image.caption}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ 
          textAlign: 'center', 
          py: { xs: 6, md: 8 },
          px: { xs: 2, md: 4 },
          mx: { xs: 1, md: 0 },
          backgroundColor: surface.sunken,
          borderRadius: 2
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: ink.secondary,
              fontSize: { xs: '1.1rem', md: '1.25rem' }
            }}
          >
            No images in this gallery yet
          </Typography>
        </Box>
      )}

      {/* Image Viewer Dialog */}
      <Dialog
        open={selectedImageIndex !== null}
        onClose={handleCloseViewer}
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: 'black',
            maxWidth: isMobile ? '100vw' : '95vw',
            maxHeight: isMobile ? '100vh' : '95vh',
            m: 0
          }
        }}
      >
        <DialogActions sx={{ 
          position: 'absolute', 
          top: { xs: 8, md: 0 }, 
          right: { xs: 8, md: 0 }, 
          zIndex: 1 
        }}>
          <IconButton 
            onClick={handleCloseViewer} 
            sx={{ 
              ...overlayButtonSx,
              size: isMobile ? 'large' : 'medium'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogActions>
        
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          position: 'relative',
          height: '100%'
        }}>
          {selectedImage && (
            <Box sx={{ 
              position: 'relative', 
              display: 'flex', 
              alignItems: 'center',
              width: '100%',
              height: '100%'
            }}>
              {/* Mobile navigation buttons */}
              {isMobile && (
                <>
                  {selectedImageIndex > 0 && (
                    <IconButton
                      onClick={handlePrevImage}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        ...overlayButtonSx,
                        zIndex: 2,
                        p: 1.5
                      }}
                    >
                      <KeyboardArrowLeft fontSize="large" />
                    </IconButton>
                  )}
                  
                  {selectedImageIndex < gallery.images.items.length - 1 && (
                    <IconButton
                      onClick={handleNextImage}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        ...overlayButtonSx,
                        zIndex: 2,
                        p: 1.5
                      }}
                    >
                      <KeyboardArrowRight fontSize="large" />
                    </IconButton>
                  )}
                </>
              )}
              
              {/* Desktop navigation buttons */}
              {!isMobile && (
                <>
                  {selectedImageIndex > 0 && (
                    <IconButton
                      onClick={handlePrevImage}
                      sx={{
                        position: 'absolute',
                        left: -60,
                        ...overlayButtonSx
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  
                  {selectedImageIndex < gallery.images.items.length - 1 && (
                    <IconButton
                      onClick={handleNextImage}
                      sx={{
                        position: 'absolute',
                        right: -60,
                        ...overlayButtonSx
                      }}
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  )}
                </>
              )}
              
              {/* Loading indicator for original image */}
              {loadingOriginal && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2
                  }}
                >
                  <CircularProgress sx={{ color: ink.onDark }} />
                </Box>
              )}
              
              {/* Original image */}
              <img
                src={getPublicGalleryImageUrl(selectedImage.originalUrl)}
                alt={selectedImage.altText || 'Gallery image'}
                style={{
                  maxWidth: isMobile ? '100vw' : '85vw',
                  maxHeight: isMobile ? '100vh' : '85vh',
                  objectFit: 'contain',
                  opacity: loadingOriginal ? 0.3 : 1,
                  transition: 'opacity 0.3s ease-in-out',
                  margin: 'auto',
                  display: 'block'
                }}
                onLoad={handleOriginalImageLoad}
              />
              
              {/* Image info overlay */}
              {(selectedImage.caption || selectedImage.altText) && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: scrimStrong,
                    color: ink.onDark,
                    px: { xs: 2, md: 2.5 },
                    py: { xs: 1.5, md: 2 }
                  }}
                >
                  {selectedImage.caption && (
                    <Typography 
                      variant="body1" 
                      gutterBottom
                      sx={{ 
                        color: ink.onDark,
                        lineHeight: 1.6,
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      {selectedImage.caption}
                    </Typography>
                  )}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      color: ink.onDarkMuted,
                      letterSpacing: '0.04em',
                      fontSize: { xs: '0.75rem', md: '0.75rem' }
                    }}
                  >
                    Image {selectedImageIndex + 1} of {gallery.images.items.length}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default GalleryViewPage;